import { Table, Button, Form } from 'antd';

import { ModalWrap } from '../component/modal-wrap';
import { useEffect, useState, Dispatch, SetStateAction } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getGauthFailList, failRetryList, getFailSureList } from '../../api';
import { IAuthFailItem, IAuthSyncReqItem } from '../../model/api-modules';
import { SystemMenuId } from '../../model/system/menu';
import { EyeOutlined } from '@ant-design/icons';
import warnImage from '../../assets/images/warn-yellow.png';
import { getLocalTime } from '../../lib';
import {
  CommonAction,
  ReduxCommonActionType,
} from '../../store/modules/common';
import { ReactJsonEditor } from '../../components/react-json-editor';
import {
  messageError,
  messageSuccess,
  messageWarning,
} from '../../components/message';
import getFormatMessage from '../../locales/get-format-message';
import { IRootState } from '../../store/reducers';

export const AdminGauthFail = () => {
  const [failList, setFailList] = useState<IAuthFailItem[]>([]);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [modalVisibleRetry, setModalVisibleRetry] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<string>();
  const [userID, setUserID] = useState<Array<string>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingTable, setLoadingTable] = useState<boolean>(false);
  const [loadingMap, setLoadingMap] = useState<{ [key: number]: boolean }>({});
  const [selectRow, setSelectRow] = useState<IAuthFailItem | null>(null);
  const [currentId, setCurrentId] = useState<number>(0);
  const [form] = Form.useForm();
  const userInfo = useSelector((state: IRootState) => state.common.userInfo);
  // 获取失败记录列表
  const getFailList = async () => {
    setLoadingTable(true);
    const res = await getGauthFailList();
    if (res) {
      setFailList(res);
    }
    setLoadingTable(false);
  };
  const menuDispatch = useDispatch<Dispatch<CommonAction>>();

  menuDispatch({
    type: ReduxCommonActionType.SELECT_MENU_ID,
    result: SystemMenuId.GauthFail,
  });
  // 失败列表
  const openValues = (record: SetStateAction<IAuthFailItem | null>) => {
    if (record) {
      setSelectRow(record);
      setModalVisible(true);
    }
  };

  // 复制
  const copySyncValue = (record: IAuthFailItem) => {
    const data = JSON.parse(record.SyncValue);
    // 只要user_name和service_code
    const syncValue = data.service_list
      .map((item: { [s: string]: unknown } | ArrayLike<unknown>) =>
        Object.values(item)
          .filter((_value, index) => index % 2 !== 0)
          .join(',')
      )
      .join('\n');
    navigator.clipboard.writeText(syncValue).then(() => {
      messageSuccess(getFormatMessage('record_copied_to_clipboard'));
    });
  };

  // 失败列表弹窗展示
  const SyncValueMore = () => {
    const closeService = () => {
      setModalVisible(false);
      form.resetFields();
    };
    const handleCopy = async () => {
      if (selectRow) {
        copySyncValue(selectRow);
      }
      return true;
    };
    let syncValueJson;
    if (selectRow?.SyncValue) {
      const data = JSON.parse(selectRow.SyncValue);
      syncValueJson = data.service_list;
    }
    if (modalVisible) {
      const maxHeight = window.innerHeight - 200;
      const modalContentStyle = {
        maxHeight: `${maxHeight}px`,
        overflow: 'auto',
      };
      return (
        <ModalWrap
          width="800px"
          cancelText={getFormatMessage('cancel')}
          oKText={getFormatMessage('copy')}
          onOk={handleCopy} // 调用复制函数
          onClose={closeService}
          title={
            <p className="fail-title">
              <span>{getFormatMessage('sync_value')}</span>
            </p>
          }
          className="fail-dialog-gauth"
        >
          <div style={modalContentStyle}>
            {syncValueJson ? (
              <ReactJsonEditor
                id="gauth-sync-json-editor"
                height={400}
                mode="preview"
                json={syncValueJson}
              />
            ) : (
              <p>{getFormatMessage('no_data_available')}</p>
            )}
          </div>
        </ModalWrap>
      );
    }
    return null;
  };

  // 获取失败重试名单前展示
  const handleRetrySure = async (id: number) => {
    try {
      setLoadingMap((prevLoadingMap) => ({
        ...prevLoadingMap,
        [id]: true,
      }));
      const res = await getFailSureList(id);
      if (res) {
        // 可重试成功
        const data = res.nextSyncUser;
        // 不可重试成功
        const failData = res.abnormalUsers;
        console.log('重试必然失败名单：', failData);
        if (data) {
          const userList = data.map(
            (item: { user_name: string }) => item.user_name
          );
          setUserID(userList);
          const withDuration = data.filter(
            (item: IAuthSyncReqItem) => item.duration
          );
          const userNamesWithDuration = withDuration
            .map((item: { user_name: string }) => item.user_name)
            .join(', ');
          const withNoDuration = data.filter(
            (item: IAuthSyncReqItem) => !item.duration
          );
          const userNamesWithNoDuration = withNoDuration
            .map((item: { user_name: string }) => item.user_name)
            .join(', ');
          if (withDuration.length > 0) {
            // 添加权限
            const modalContents = getFormatMessage('add_permission_list', {
              params: {
                userNamesWithDuration,
              },
            });
            setModalContent(modalContents);
          }
          if (withNoDuration.length > 0) {
            // 删除权限
            const modalContents = getFormatMessage('remove_permission_list', {
              params: {
                userNamesWithNoDuration,
              },
            });
            setModalContent(modalContents);
          }
          setCurrentId(id);
          setModalVisibleRetry(true);
        } else {
          messageWarning(getFormatMessage('no_list_retry'));
        }
      }
    } catch (error) {
      messageError(getFormatMessage('fail_retrieable_list'));
    } finally {
      setLoadingMap((prevLoadingMap) => ({
        ...prevLoadingMap,
        [id]: false,
      }));
    }
  };

  // 重试操作
  const handleRetry = async () => {
    try {
      setLoading(true);
      await failRetryList(currentId, userID);
      messageSuccess(getFormatMessage('retry_success'));
      getFailList();
      setModalVisibleRetry(false);
      return true;
    } catch (error: any) {
      messageError(error?.apiError?.message || getFormatMessage('retry_fail'));
      setModalVisibleRetry(false);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // 弹窗提示名单信息
  const RetryModal = () => {
    // console.log('id:',record.id);
    if (modalVisibleRetry) {
      return (
        <ModalWrap
          width="550px"
          cancelText={getFormatMessage('cancel')}
          oKText={getFormatMessage('confirm')}
          onClose={closeRetry}
          onOk={handleRetry}
          loading={loading}
          title={
            <div className="fail-title">
              <img
                src={warnImage}
                alt="Warning"
                style={{ marginRight: '10px', marginTop: '-2px' }}
              />
              {getFormatMessage('retry_to_sync')}
            </div>
          }
          className="fail-dialog"
        >
          <div
            style={{
              whiteSpace: 'pre-wrap',
              marginLeft: '20px',
              marginRight: '20px',
            }}
          >
            {modalContent}
          </div>
        </ModalWrap>
      );
    }
    return null;
  };

  // 关闭重试失败提示
  const closeRetry = () => {
    setModalVisibleRetry(false);
    setModalContent('');
  };

  const COLUMNS = [
    {
      title: getFormatMessage('id'),
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: getFormatMessage('status'),
      dataIndex: 'Status',
      key: 'Status',
      render: (text: number) => {
        if (text === 0) {
          return <span>{getFormatMessage('wait_for_retry')}</span>;
        }
        if (text === 1) {
          return <span>{getFormatMessage('successful_retry')}</span>;
        }
        return <span>{getFormatMessage('unknown_status')}</span>;
      },
    },
    {
      title: getFormatMessage('business'),
      dataIndex: 'Business',
      key: 'Business',
    },
    {
      title: getFormatMessage('founder_id'),
      dataIndex: 'FounderID',
      key: 'FounderID',
    },
    {
      title: getFormatMessage('founder_name'),
      dataIndex: 'FounderName',
      key: 'FounderName',
    },
    {
      title: getFormatMessage('create_time_local'),
      dataIndex: 'CreateTime',
      key: 'CreateTime',
      render: (text: Date) => {
        return getLocalTime(text, userInfo?.UserSettings?.Timezone);
      },
    },
    {
      title: getFormatMessage('modify_time_local'),
      dataIndex: 'ModifyTime',
      key: 'ModifyTime',
      render: (text: Date) => {
        return getLocalTime(text, userInfo?.UserSettings?.Timezone);
      },
    },
    {
      title: getFormatMessage('sync_value'),
      key: 'action',
      render: (_text: string, record: IAuthFailItem) => (
        <div>
          <EyeOutlined
            onClick={() => openValues(record)}
            style={{ position: 'relative', top: -3, marginLeft: 25 }}
          />
        </div>
      ),
    },
    {
      title: getFormatMessage('operate'),
      key: 'Operate',
      render: (_text: string, record: IAuthFailItem) => {
        const isRetryDisabled = record.Status === 1;
        return (
          <div>
            {isRetryDisabled ? (
              <span></span>
            ) : (
              <Button
                loading={loadingMap[record.id]}
                className="gc-btn gc-btn-primary btn-admin is-small"
                onClick={() => handleRetrySure(record.id)}
              >
                {getFormatMessage('retry')}
              </Button>
            )}
          </div>
        );
      },
    },
  ];
  useEffect(() => {
    getFailList();
  }, []);

  return (
    <div>
      <Form form={form} component={false}>
        <Table
          rowKey="id"
          loading={loadingTable}
          dataSource={failList || []}
          columns={COLUMNS}
          pagination={{
            hideOnSinglePage: true,
            showTotal: (total) => {
              return getFormatMessage('total_items', {
                params: {
                  total,
                },
              });
            },
            pageSize: 100,
          }}
        />
      </Form>
      {SyncValueMore()}
      {RetryModal()}
    </div>
  );
};
